import React, { useContext, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import { useTitleContext } from '~/dashboard/PageTitleProvider';
import { routes } from '~/helpers/routes';

import WrappedRerunEvaluationForm from './form';
import { isPermittedInternalRedirectPath } from './helpers';
import { useRerunEvaluationData } from './hooks';

export const RerunEvaluationView = () => {
  const { entityToken, evaluationToken } = useParams<{
    entityToken: string;
    evaluationToken: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { typhonDomain, permissions } = useContext(UserContext);
  const { setTitlePrefix } = useTitleContext();

  const isChildRun = searchParams.get('childRun') === 'true';
  const groupId = searchParams.get('groupId') || false;
  const groupEntityTokenWithWhichToRerunEvaluation = searchParams.get('groupEntityToken') || undefined;
  const doPassOriginalEvaluationToken = searchParams.get('passOriginalEvaluationToken') === 'true';
  const isSwitchWorkflowEnabled = searchParams.get('switchWorkflow') !== 'false';
  // Set only when launched from the frontend-dashboard journey application review page (via
  // EvaluationHeaderMenu/PartialDetails' `buildRerunEvaluationUrl`) — redirects back there
  // (with a success flag) instead of this rerun's new standalone evaluation page. Absent for
  // every other caller of this view, which keeps today's behavior unchanged for them.
  const redirectTo = searchParams.get('redirectTo');

  const { data, isLoading } = useRerunEvaluationData({ entityToken, evaluationToken });

  useEffect(() => {
    setTitlePrefix(isChildRun ? 'Run Child Entity' : 'Rerun Evaluation');
  }, [isChildRun, setTitlePrefix]);

  const handleCancel = () => {
    if (redirectTo && isPermittedInternalRedirectPath(redirectTo)) {
      window.location.assign(redirectTo);
      return;
    }

    if (entityToken && evaluationToken) {
      navigate(routes.entities.evaluation.detail({ entityToken, evaluationToken }).replace('?json=true', ''));
    } else {
      navigate(routes.entities.list);
    }
  };

  const handleRerunEvaluation = (response: { evaluationUrl?: string }) => {
    if (redirectTo && isPermittedInternalRedirectPath(redirectTo)) {
      window.location.assign(redirectTo);
      return;
    }

    if (response.evaluationUrl) {
      navigate(response.evaluationUrl);
    }
  };

  if (isLoading || !data) {
    return <Spinner />;
  }

  return (
    <WrappedRerunEvaluationForm
      key={`${evaluationToken}-${isChildRun}`}
      agentEmail={data.agentEmail}
      applications={data.applications}
      apiUrl={typhonDomain}
      bearerToken={data.bearerToken}
      documentTypes={data.documentTypes}
      entity={data.entity}
      evaluation={data.evaluation}
      groupId={groupId}
      isChildRun={isChildRun}
      isSwitchWorkflowEnabled={isSwitchWorkflowEnabled}
      // permissionsMixin only picks permissions up from context on a later update, which
      // would flash the permission-gated settings fields on first render.
      permissions={permissions}
      doPassOriginalEvaluationToken={doPassOriginalEvaluationToken}
      groupEntityTokenWithWhichToRerunEvaluation={groupEntityTokenWithWhichToRerunEvaluation}
      onCancel={handleCancel}
      onRerunEvaluation={handleRerunEvaluation}
    />
  );
};
